import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ChatChannelResponse, GuildSummary } from '@/lib/api';
import { ChannelListItem } from './ChannelListItem';
import { GuildChannelGroup } from './GuildChannelGroup';
import { useExpandedGuilds } from '../hooks/useExpandedGuilds';

function channelActivityTime(channel: ChatChannelResponse): string {
  return channel.lastMessage?.createdAt ?? channel.updatedAt;
}

function byRecentActivity(a: ChatChannelResponse, b: ChatChannelResponse): number {
  return channelActivityTime(b).localeCompare(channelActivityTime(a));
}

interface GuildGroup {
  guildId: string;
  name: string;
  /** The guild's group/private channels. */
  channels: ChatChannelResponse[];
  /** DMs filed under this guild, kept apart from the user's personal DMs. */
  directChannels: ChatChannelResponse[];
  /** Most recent activity across everything in the guild — orders guilds. */
  latestActivity: string;
}

/**
 * Buckets every guild-scoped channel under its guild. Keyed on `guildId` rather
 * than `channelType === 'guild'` so a group chat created inside a guild nests
 * with its siblings; a channel with no `guildId` is left for the flat sections.
 */
function groupChannelsByGuild(
  channels: ChatChannelResponse[],
  guildNameById: Map<string, string>,
): GuildGroup[] {
  const byGuild = new Map<string, ChatChannelResponse[]>();
  for (const channel of channels) {
    if (!channel.guildId) continue;
    const existing = byGuild.get(channel.guildId);
    if (existing) existing.push(channel);
    else byGuild.set(channel.guildId, [channel]);
  }

  return Array.from(byGuild.entries())
    .map(([guildId, guildChannels]) => ({
      guildId,
      // A guild the user holds channels in but that isn't in their guild list
      // (still loading, or membership since revoked) still gets a usable label.
      name: guildNameById.get(guildId) ?? 'Guild',
      channels: guildChannels.filter((c) => c.channelType !== 'direct').sort(pinnedFirst),
      directChannels: guildChannels.filter((c) => c.channelType === 'direct').sort(pinnedFirst),
      latestActivity: guildChannels.reduce(
        (latest, c) => (channelActivityTime(c) > latest ? channelActivityTime(c) : latest),
        '',
      ),
    }))
    .sort(
      (a, b) => b.latestActivity.localeCompare(a.latestActivity) || a.name.localeCompare(b.name),
    );
}

function pinnedFirst(a: ChatChannelResponse, b: ChatChannelResponse): number {
  const pinA = a.isPinned ? 1 : 0;
  const pinB = b.isPinned ? 1 : 0;
  if (pinB !== pinA) return pinB - pinA;
  return byRecentActivity(a, b);
}

interface ChannelListProps {
  channels: ChatChannelResponse[];
  activeChannelId: string | null;
  onSelectChannel: (channelId: string) => void;
  /** Press-and-hold a channel to peek at its messages without marking them read. */
  onPreviewChannel?: (channel: ChatChannelResponse) => void;
  loading: boolean;
  viewMode?: 'sections' | 'unified';
  /** Supplies guild names for the collapsible guild groups. */
  guilds?: GuildSummary[];
  /** Opens the guild-member picker for starting a guild-scoped DM. */
  onNewGuildDirectMessage?: (guildId: string, guildName: string) => void;
}

function SectionLabel({ label }: { label: string }) {
  return (
    <div className="px-4 py-2 text-[10px] font-normal text-muted-foreground uppercase tracking-wider">
      {label}
    </div>
  );
}

export function ChannelList({
  channels: initialChannels,
  activeChannelId,
  onSelectChannel,
  onPreviewChannel,
  loading,
  viewMode = 'unified',
  guilds = [],
  onNewGuildDirectMessage,
}: ChannelListProps) {
  const [pinOverrides, setPinOverrides] = useState<Record<string, boolean>>({});
  const { isExpanded, setExpanded } = useExpandedGuilds();

  const channels = initialChannels.map((c) =>
    c.channelId in pinOverrides ? { ...c, isPinned: pinOverrides[c.channelId] } : c,
  );

  // Clear overrides when the server data has caught up, or when a channel leaves the list
  useEffect(() => {
    setPinOverrides((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const channelById = new Map(initialChannels.map((c) => [c.channelId, c]));
      const next = { ...prev };
      let changed = false;
      for (const id of Object.keys(next)) {
        const ch = channelById.get(id);
        if (!ch || next[id] === ch.isPinned) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [initialChannels]);

  const handlePinToggled = useCallback((channelId: string, isPinned: boolean) => {
    setPinOverrides((prev) => ({ ...prev, [channelId]: isPinned }));
  }, []);

  const handlePinFailed = useCallback((channelId: string) => {
    setPinOverrides((prev) => {
      const next = { ...prev };
      delete next[channelId];
      return next;
    });
  }, []);

  if (loading) {
    return (
      <div className="p-4 space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="animate-pulse flex items-center gap-3 p-2">
            <div className="h-10 w-10 bg-muted rounded-full" />
            <div className="flex-1">
              <div className="h-4 bg-muted rounded w-24 mb-2" />
              <div className="h-3 bg-muted rounded w-32" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (channels.length === 0) {
    return <div className="p-4 text-center text-sm text-muted-foreground">No channels yet</div>;
  }

  // Guild-scoped channels nest under their guild; everything else keeps the flat
  // sections. Partitioning on `guildId` first means no channel can fall through
  // both branches — a guild-typed channel with no guildId still lands in Groups.
  const unscoped = channels.filter((c) => !c.guildId);
  const communityChannels = unscoped.filter((c) => c.channelType === 'global');
  const groupChannels = unscoped
    .filter((c) => c.channelType === 'group' || c.channelType === 'guild')
    .sort(pinnedFirst);
  const directChannels = unscoped.filter((c) => c.channelType === 'direct').sort(pinnedFirst);

  const guildNameById = new Map(guilds.map((g) => [g.guildId, g.name]));
  const guildGroups = groupChannelsByGuild(channels, guildNameById);

  // Default the guild holding the active channel to open, so selecting a channel
  // never hides it. An explicit toggle (persisted) always wins over that default.
  const activeGuildId = channels.find((c) => c.channelId === activeChannelId)?.guildId ?? null;

  const renderItem = (channel: ChatChannelResponse, showPrivateGlyph = false) => (
    <ChannelListItem
      key={channel.channelId}
      channel={channel}
      isActive={channel.channelId === activeChannelId}
      onClick={() => onSelectChannel(channel.channelId)}
      onPreview={onPreviewChannel}
      onPinToggled={handlePinToggled}
      onPinFailed={handlePinFailed}
      showPrivateGlyph={showPrivateGlyph}
    />
  );

  const renderGuildGroup = (group: GuildGroup) => {
    const expanded = isExpanded(group.guildId) ?? group.guildId === activeGuildId;
    return (
      <GuildChannelGroup
        key={group.guildId}
        guildId={group.guildId}
        name={group.name}
        channels={group.channels}
        directChannels={group.directChannels}
        expanded={expanded}
        onToggle={() => setExpanded(group.guildId, !expanded)}
        activeChannelId={activeChannelId}
        // The lock glyph disambiguates private channels from open ones; guild
        // DMs are all private by construction, so showing it on every DM row
        // would be pure noise — channel-type rows only.
        renderChannel={(channel) => renderItem(channel, channel.channelType !== 'direct')}
        onNewDirectMessage={
          onNewGuildDirectMessage
            ? () => onNewGuildDirectMessage(group.guildId, group.name)
            : undefined
        }
      />
    );
  };

  if (viewMode === 'unified') {
    // One list ordered purely by recent activity — no type sections. Guild-scoped
    // channels still nest under their collapsible guild node (which sorts by the
    // guild's latest activity); community/group/DM channels are flat rows. Pinned
    // channels float to the top (a guild group is never pinned), matching the
    // sections view's within-bucket behaviour.
    const unifiedEntries: { key: string; pinned: boolean; activity: string; node: ReactNode }[] = [
      ...communityChannels,
      ...groupChannels,
      ...directChannels,
    ]
      .map((c) => ({
        key: c.channelId,
        pinned: c.isPinned ?? false,
        activity: channelActivityTime(c),
        node: renderItem(c),
      }))
      .concat(
        guildGroups.map((g) => ({
          key: g.guildId,
          pinned: false,
          activity: g.latestActivity,
          node: renderGuildGroup(g),
        })),
      )
      .sort((a, b) => {
        const pin = (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
        return pin !== 0 ? pin : b.activity.localeCompare(a.activity);
      });

    return (
      <div className="flex flex-col h-full overflow-y-auto">
        <div className="p-2 flex flex-col gap-1">{unifiedEntries.map((e) => e.node)}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto gap-4">
      {communityChannels.length > 0 && (
        <div className="p-2">
          <div className="mt-2">
            <SectionLabel label="Community" />
          </div>
          <div className="flex flex-col gap-1">{communityChannels.map((c) => renderItem(c))}</div>
        </div>
      )}
      {guildGroups.length > 0 && (
        <div className="p-2">
          <SectionLabel label="Guilds" />
          <div className="flex flex-col gap-1">{guildGroups.map(renderGuildGroup)}</div>
        </div>
      )}
      {groupChannels.length > 0 && (
        <div className="p-2">
          <SectionLabel label="Groups" />
          <div className="flex flex-col gap-1">{groupChannels.map((c) => renderItem(c))}</div>
        </div>
      )}
      {directChannels.length > 0 && (
        <div className="p-2">
          <SectionLabel label="Direct Messages" />
          <div className="flex flex-col gap-1">{directChannels.map((c) => renderItem(c))}</div>
        </div>
      )}
    </div>
  );
}
